import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import {
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { SPACING } from '@common/theme/spacing'
import { AddRounded, East } from '@mui/icons-material'
import { Fragment } from 'react/jsx-runtime'
import CreateStudySubjectDialog from '@features/study_details/Subjects/components/CreateStudySubjectDialog'
import { queryClient } from '@common/config/api/queryClient'
import { useState } from 'react'
import { useGetSubjectsList } from '@features/subject/hooks/useGetSubjectsList'
import { useParams } from 'react-router-dom'
import { useSubjectReassignementMutation } from '@features/qc_task_details_view/hooks/useSubjectReassignementMutation'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { TimepointPermission } from '@auth/permissionsEnum'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  width: 888,
}

export default function SubjectReassignModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [isSubjectDialogVisible, setIsSubjectDialogVisible] = useState(false)
  const [selectedSubject, setSelectedSubject] = useState<string>('')
  const [reassignOption, setReassignOption] = useState<'move' | 'duplicate'>('duplicate')
  const { mutateAsync: subjectReassignement } = useSubjectReassignementMutation()
  const { hasPermission } = useCombinedPermissions()

  const { studyId, subjectId } = useParams<{
    studyId: string
    subjectId: string
  }>()
  const { data } = useGetSubjectsList(['SubjectsList'], {
    study_id: studyId!,
  })

  const subjects =
    data?.data?.subjects.filter((subject) =>
      subject.study_timepoints.every((timepoint) => timepoint.timepoint_submissions.length === 0),
    ) || []

  const currentSubject = data?.data?.subjects.find((subject) => subject.subject_id === subjectId)

  const move_items: string[] = [
    'Site information',
    'Subject status',
    'Subject eForm',
    'Source exams',
    'QC results',
    'Read results',
  ]

  const duplicate_items: string[] = ['Site information', 'Subject status', 'Subject eForm', 'Source exams']

  const handleReassign = async () => {
    if (selectedSubject) {
      await subjectReassignement({
        path: {
          study_id: studyId!,
          subject_id: subjectId!,
        },
        body: {
          to_subject_id: selectedSubject,
          is_duplicate: reassignOption === 'duplicate',
        },
      })
      queryClient.invalidateQueries({ queryKey: ['SubjectsList'] })
      onClose()
    }
  }

  return (
    <Fragment>
      <Modal open={open}>
        <Paper sx={modalStyle}>
          <Stack padding={SPACING.spacingXxl} alignSelf="stretch" alignItems="flex-start">
            <Typography variant="h5" color="text.primary">
              Reassign subject data?
            </Typography>
          </Stack>
          <Stack paddingX={SPACING.spacingXxl} alignItems="center" alignSelf="stretch">
            <Stack direction="row" alignItems="center" alignSelf="stretch">
              <TextField fullWidth value={currentSubject?.pseudo_id} disabled label="Current status" size="small" />
              <Stack padding={1}>
                <East />
              </Stack>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value as string)}
              >
                <MenuItem value="" disabled>
                  <em>Select a subject</em>
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.subject_id} value={subject.subject_id}>
                    {subject.pseudo_id}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack alignItems="center" alignSelf="stretch">
              <Button
                variant="text"
                color="secondary"
                startIcon={<AddRounded />}
                sx={{
                  marginLeft: 22,
                }}
                onClick={() => {
                  setIsSubjectDialogVisible(true)
                }}
              >
                <Typography variant="body2" color="text.primary">
                  Add new subject
                </Typography>
              </Button>
            </Stack>

            <FormControl fullWidth>
              <RadioGroup
                value={reassignOption}
                onChange={(e) => setReassignOption(e.target.value as 'move' | 'duplicate')}
              >
                <Stack alignItems="flex-start" alignSelf="stretch" spacing={1}>
                  <Paper
                    sx={{
                      width: '100%',
                      borderRadius: 3,
                      padding: SPACING.spacingXl,
                      border: '1px solid',
                      borderColor: reassignOption === 'move' ? '#006B8C' : 'divider',
                    }}
                  >
                    <FormControlLabel
                      value="move"
                      control={<Radio />}
                      disabled={!hasPermission(TimepointPermission.ALL)}
                      label={
                        <Stack>
                          <Typography variant="body1" color="text.primary">
                            Completely reassign all data to a new subject
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Including existing QC and Read tasks and their results. The original subject will no longer
                            retain this data.
                          </Typography>
                          <List>
                            {move_items.map((item, index) => (
                              <ListItem key={index} disablePadding>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" color="text.primary">
                                      {`• ${item}`}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Stack>
                      }
                    />
                  </Paper>
                  <Paper
                    sx={{
                      width: '100%',
                      borderRadius: 3,
                      padding: SPACING.spacingXl,
                      border: '1px solid',
                      borderColor: reassignOption === 'duplicate' ? '#006B8C' : 'divider',
                    }}
                  >
                    <FormControlLabel
                      value="duplicate"
                      control={<Radio />}
                      label={
                        <Stack>
                          <Typography variant="body1" color="text.primary">
                            Duplicate main data to a new subject
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Except existing QC and Read tasks and their results. The original subject’s data will remain
                            unchanged.
                          </Typography>
                          <List>
                            {duplicate_items.map((item, index) => (
                              <ListItem key={index} disablePadding>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" color="text.primary">
                                      {`• ${item}`}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Stack>
                      }
                    />
                  </Paper>
                </Stack>
              </RadioGroup>
            </FormControl>
          </Stack>
          <Stack
            justifyContent="flex-end"
            direction="row"
            alignSelf="stretch"
            alignItems="center"
            padding={SPACING.spacingXxl}
            spacing={SPACING.spacingMd}
          >
            <Button
              variant="contained"
              sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
              disabled={!selectedSubject}
              onClick={handleReassign}
            >
              Reassign data
            </Button>
          </Stack>
        </Paper>
      </Modal>

      {isSubjectDialogVisible ? (
        <CreateStudySubjectDialog
          onClose={(data) => {
            setIsSubjectDialogVisible(false)
            if (data) {
              const newSubjectId = 'id' in data ? data.id : ''
              setSelectedSubject(newSubjectId)
              queryClient.invalidateQueries({ queryKey: ['SubjectsList'] })
            }
          }}
        />
      ) : null}
    </Fragment>
  )
}
